import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { GrowthProfileGrowthType } from '@/types/schema';

import { growthProfileTypeOptions } from './GrowthProfileModal.constants';
import { useGrowthProfileModalContext } from './GrowthProfileModal.context';
import { GrowthProfileModalTable } from './GrowthProfileModal.table';
import {
  GrowthProfileModalFormPaths,
  GrowthProfileModalFormShape,
} from './GrowthProfileModal.types';
import { TaxDragEditor } from './taxDrag/TaxDragEditor';
import { TaxDragSummaryCard } from './taxDrag/TaxDragSummaryCard';

interface GrowthProfileModalBodyProps {
  isEditingTaxDrag: boolean;
  onEditTaxDrag: () => void;
}

export function GrowthProfileModalBody({
  isEditingTaxDrag,
  onEditTaxDrag,
}: GrowthProfileModalBodyProps) {
  const { control } = useFormContext<GrowthProfileModalFormShape>();
  const isIncomeTaxEnabled = useFeatureFlag('trust_income_tax');
  const { growthProfileId, isDefault, isDuplicating } =
    useGrowthProfileModalContext();

  const modalTitle = useMemo(() => {
    if (isDuplicating) return 'Duplicate growth profile';
    if (isDefault) return 'Default growth profile';
    if (growthProfileId) return 'Edit growth profile';
    return 'Create growth profile';
  }, [isDefault, growthProfileId, isDuplicating]);

  if (isIncomeTaxEnabled && isEditingTaxDrag) {
    return <TaxDragEditor />;
  }

  return (
    <Stack
      direction="column"
      spacing={isIncomeTaxEnabled ? 3 : 2}
      pt={isIncomeTaxEnabled ? 5 : 2}
      pb={4}
    >
      <Card
        variant="filled"
        sx={isIncomeTaxEnabled ? { p: 0, bgcolor: 'transparent' } : { p: 2 }}
      >
        <Stack spacing={2}>
          {!isIncomeTaxEnabled && (
            <Typography variant="title-xl">{modalTitle}</Typography>
          )}
          <Box
            sx={
              isIncomeTaxEnabled
                ? { '& label': { typography: 'title-m' } }
                : undefined
            }
          >
            <FormLayoutRow sx={{ '&:not(:last-child)': { mb: 4 } }}>
              <FormLayoutItem width={12}>
                <Box sx={{ maxWidth: isIncomeTaxEnabled ? 600 : undefined }}>
                  <FormAwareTextInput<GrowthProfileModalFormShape>
                    control={control}
                    fieldName={
                      'displayName' as const satisfies GrowthProfileModalFormPaths
                    }
                    label="Name"
                    required
                  />
                </Box>
              </FormLayoutItem>
            </FormLayoutRow>
            <FormLayoutRow>
              <FormLayoutItem width={12}>
                <Box sx={{ maxWidth: isIncomeTaxEnabled ? 212 : undefined }}>
                  <FormAwarePercentInput<GrowthProfileModalFormShape>
                    control={control}
                    fieldName={
                      'exemptionGrowthRate' as const satisfies GrowthProfileModalFormPaths
                    }
                    label="Inflation rate"
                    required
                    isDecimalJSInput
                    fixedDecimalScale
                    decimalScale={2}
                    contextualHelp={
                      <PopperContent body="The inflation rate entered here will also apply to exemption growth rates in calculations." />
                    }
                  />
                </Box>
              </FormLayoutItem>
            </FormLayoutRow>
          </Box>
          {!isIncomeTaxEnabled && <GrowthTypeCard />}
        </Stack>
      </Card>
      {isIncomeTaxEnabled && (
        <>
          <TaxDragSummaryCard onEdit={onEditTaxDrag} />
          <Card
            variant="outlined"
            sx={{ p: 2, borderRadius: 2 }}
            data-testid="pre-tax-growth-card"
          >
            <Stack spacing={3}>
              <Typography variant="title-m">Pre-tax growth rate</Typography>
              <GrowthTypeCard />
              <GrowthProfileModalTable embedded />
            </Stack>
          </Card>
        </>
      )}
      {!isIncomeTaxEnabled && <GrowthProfileModalTable />}
    </Stack>
  );
}

function GrowthTypeCard() {
  const { control } = useFormContext<GrowthProfileModalFormShape>();
  const { isDefault } = useGrowthProfileModalContext();
  const isIncomeTaxEnabled = useFeatureFlag('trust_income_tax');

  const [growthType] = useWatch({ control, name: ['growthType'] });

  const shouldDisplayGlobalGrowthInput =
    growthType === GrowthProfileGrowthType.GlobalGrowth;

  return (
    <Card
      variant={isIncomeTaxEnabled ? 'transparent' : 'filled'}
      sx={{ p: isIncomeTaxEnabled ? 0 : 2 }}
    >
      <Stack direction="column" spacing={2}>
        <Box
          sx={
            isIncomeTaxEnabled
              ? { p: 2, bgcolor: 'background.default', borderRadius: 2 }
              : undefined
          }
        >
          <FormAwareRadioGroup<GrowthProfileModalFormShape>
            data-testid="growth-type-radio-group"
            disabled={isDefault}
            control={control}
            fieldName={
              'growthType' as const satisfies GrowthProfileModalFormPaths
            }
            options={growthProfileTypeOptions}
            row={false}
            columnSpacing={isIncomeTaxEnabled ? 3 : 2}
            size="small"
            sx={{ ml: 0 }}
          />
        </Box>
        {shouldDisplayGlobalGrowthInput && (
          <FormLayoutRow>
            <FormLayoutItem width={3}>
              <Box pl={3}>
                <FormAwarePercentInput<GrowthProfileModalFormShape>
                  control={control}
                  fieldName={
                    'assetGrowthReturn' as const satisfies GrowthProfileModalFormPaths
                  }
                  label="Global growth rate"
                  required
                  isDecimalJSInput
                  hideLabel
                  fixedDecimalScale
                  decimalScale={2}
                />
              </Box>
            </FormLayoutItem>
          </FormLayoutRow>
        )}
      </Stack>
    </Card>
  );
}
